import React, { useEffect } from 'react';

// Shared layout helpers used by every reading Part screen so they stay
// visually consistent without each file duplicating ~80 lines of chrome.
//
//   <Frame>          — outer dark-gradient page background + content max-width
//   <TopBar>         — back button, EN ⇄ AR toggle, theme toggle, profile
//   <PartHeader>     — coloured part-N tile + part name + question counter +
//                      progress dots (correct = green, wrong = red)
//   <ConfirmModal>   — themed Yes/No dialog so we never use window.confirm
//   PART_PALETTE     — per-part fg/bg colour tokens (P1 indigo, P2 blue, …)

export const PART_PALETTE = {
  1: { fg: '#5B52F0', bg: '#EEF2FF' },
  2: { fg: '#3E6FB0', bg: '#E8F0FA' },
  3: { fg: '#A86FB3', bg: '#F4ECF7' },
  4: { fg: '#4E8A5E', bg: '#E9F2E4' },
  5: { fg: '#B55A48', bg: '#F7E6DF' },
  6: { fg: '#0D9488', bg: '#CCFBF1' },
};

export function Frame({ theme, isMobile, children, maxWidth = 720 }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${theme.pageBgAlt} 0%, ${theme.pageBgSoft} 100%)`,
      fontFamily: "'DM Sans', 'Outfit', sans-serif",
      color: theme.text,
    }}>
      <div style={{ maxWidth, margin: '0 auto', padding: isMobile ? '22px 16px 36px' : '32px 22px 56px' }}>
        {children}
      </div>
    </div>
  );
}

export function TopBar({ theme, isDark, accountControl, toggleTheme, onBack, backLabel = 'Reading home', arabicOn, onArabicToggle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          height: 42, borderRadius: 999,
          border: `1px solid ${theme.border}`, background: theme.surface,
          color: theme.body, padding: '0 16px',
          fontWeight: 800, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontFamily: 'inherit',
        }}
      >
        <span aria-hidden="true">←</span>
        <span>{backLabel}</span>
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {onArabicToggle && (
          <button
            type="button"
            onClick={onArabicToggle}
            title={`Arabic translations: ${arabicOn ? 'on' : 'off'}`}
            style={{
              height: 36, padding: '0 10px', borderRadius: 10,
              border: `1px solid ${arabicOn ? theme.accent : theme.border}`,
              background: arabicOn ? theme.accentSoftBg : theme.surface,
              color: arabicOn ? theme.accent : theme.muted,
              fontSize: 11, fontWeight: 800, letterSpacing: '.06em',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ opacity: arabicOn ? 1 : 0.55 }}>EN</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ opacity: arabicOn ? 1 : 0.55 }}>AR</span>
          </button>
        )}
        {accountControl}
      </div>
    </div>
  );
}

// Standard button row at the bottom of every part's results screen.
//   - "Try again"            — replays the current part (always)
//   - "Continue to Part N+1" (CTA) — only when onContinue is provided
//   - "Back to reading home" — only when onContinue is NOT provided
export function ResultActions({ theme, onTryAgain, onContinue, continueLabel, onBack }) {
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={onTryAgain}
        style={{
          height: 46, padding: '0 20px', borderRadius: 12,
          border: `1px solid ${theme.border}`, background: theme.surface,
          color: theme.body, fontWeight: 700, fontSize: 14, cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Try again
      </button>
      {onContinue ? (
        <>
          <button
            type="button"
            onClick={onBack}
            style={{
              height: 46, padding: '0 16px', borderRadius: 12,
              border: `1px solid ${theme.border}`, background: theme.surface,
              color: theme.body, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Reading home
          </button>
          <button
            type="button"
            onClick={onContinue}
            style={{
              height: 46, padding: '0 22px', borderRadius: 12, border: 'none',
              background: theme.ctaBg, color: theme.ctaText, fontWeight: 800,
              fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '.02em',
            }}
          >
            {continueLabel || 'Continue →'}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onBack}
          style={{
            height: 46, padding: '0 22px', borderRadius: 12, border: 'none',
            background: theme.ctaBg, color: theme.ctaText, fontWeight: 800,
            fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: '.02em',
          }}
        >
          Back to reading home
        </button>
      )}
    </div>
  );
}

// Part header with the coloured number tile + name + question counter +
// progress dots. `dots` is an array of states matching the question count:
//   'idle' (current or untouched) | 'correct' | 'wrong'
export function PartHeader({ theme, isMobile, partNumber, partName, currentIdx, total, dots }) {
  const palette = PART_PALETTE[partNumber] || { fg: theme.accent, bg: theme.accentSoftBg };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ width: 36, height: 36, borderRadius: 10, background: palette.bg, color: palette.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 16 }}>{partNumber}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: palette.fg }}>Part {partNumber}</div>
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: theme.text, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{partName}</div>
        </div>
        <div style={{ flex: 1 }} />
        {Number.isFinite(currentIdx) && Number.isFinite(total) && total > 0 && (
          <div style={{ fontSize: 13, color: theme.muted, fontVariantNumeric: 'tabular-nums', fontWeight: 700, flexShrink: 0 }}>
            Q {currentIdx + 1} of {total}
          </div>
        )}
      </div>
      {Array.isArray(dots) && dots.length > 0 && (
        <div style={{ display: 'flex', gap: 4 }}>
          {dots.map((state, i) => {
            const here = i === currentIdx;
            const bg =
              state === 'correct' ? '#22c55e'
              : state === 'wrong'   ? '#ef4444'
              : here                ? palette.fg
              : theme.border;
            return <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: bg, transition: 'background .2s ease' }} />;
          })}
        </div>
      )}
    </div>
  );
}

// Themed confirm dialog. Use instead of window.confirm so the message,
// buttons and tone all match the reading flow's look.
//
// Usage pattern with local state:
//   const [confirm, setConfirm] = useState(null);
//   // trigger:
//   setConfirm({ title, message, confirmLabel, tone: 'danger', onConfirm });
//   // render once near the bottom of the component tree:
//   <ConfirmModal state={confirm} onClose={() => setConfirm(null)} theme={theme} />
//
// `tone` controls the confirm-button colour: 'default' uses theme.ctaBg,
// 'danger' uses red. When the user confirms we call state.onConfirm() and
// then auto-close.
export function ConfirmModal({ state, onClose, theme }) {
  const open = !!state;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'Enter') {
        if (state?.onConfirm) state.onConfirm();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, state, onClose]);

  if (!open) return null;

  const tone = state.tone || 'default';
  const confirmBg = tone === 'danger' ? '#ef4444' : theme.ctaBg;
  const confirmColor = tone === 'danger' ? '#fff' : theme.ctaText;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, .55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: 'fetConfirmFade .15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fet-confirm-title"
        style={{
          background: theme.surface,
          color: theme.text,
          borderRadius: 18,
          border: `1px solid ${theme.border}`,
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
          maxWidth: 440, width: '100%',
          padding: 24,
          fontFamily: 'inherit',
        }}
      >
        {state.icon !== false && (
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: tone === 'danger' ? '#FEF2F2' : theme.accentSoftBg,
            color: tone === 'danger' ? '#dc2626' : theme.accent,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 800, marginBottom: 14,
          }}>
            {state.icon || (tone === 'danger' ? '⚠' : '?')}
          </div>
        )}
        <h2 id="fet-confirm-title" style={{ margin: 0, fontSize: 18, fontWeight: 800, color: theme.text, letterSpacing: '-.3px' }}>
          {state.title || 'Are you sure?'}
        </h2>
        {state.message && (
          <div style={{ marginTop: 8, fontSize: 14, color: theme.body, lineHeight: 1.55 }}>
            {state.message}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 42, padding: '0 18px', borderRadius: 10,
              border: `1px solid ${theme.border}`, background: theme.surface,
              color: theme.body, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {state.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => {
              if (state.onConfirm) state.onConfirm();
              onClose();
            }}
            style={{
              height: 42, padding: '0 20px', borderRadius: 10, border: 'none',
              background: confirmBg, color: confirmColor,
              fontWeight: 800, fontSize: 13, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '.02em',
            }}
          >
            {state.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
